/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import styled from 'styled-components'
import RetweetIcon from '@bufferapp/ui/Icon/Icons/Retweet'

import CloseButton from './shared/CloseButton'
import ComposerActionCreators from '../action-creators/ComposerActionCreators'
import { AttachmentTypes } from '../AppConstants'
import type { RetweetMedia } from '../entities/factories'

import styles from './css/RetweetAttachment.module.css'
import AppStore from '../stores/AppStore'
import { Tooltip } from '@buffer-mono/popcorn'

const StyledCloseButton = styled(CloseButton)`
  position: absolute;
  top: 12px;
  right: 16px;
`

interface RetweetAttachmentProps {
  draft: any
  disabled?: boolean
}

const RetweetAttachment = ({
  draft,
  disabled = false,
}: RetweetAttachmentProps): JSX.Element | null => {
  const onCloseButtonClick = (): void => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    ComposerActionCreators.toggleAttachment(draft.id, AttachmentTypes.RETWEET)
  }

  const hasRetweetAttachment =
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-call
    draft.hasRetweetAttachment && draft.hasRetweetAttachment()

  if (
    !disabled &&
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    (AppStore.getExpandedComposerId() !== draft.id || !hasRetweetAttachment)
  ) {
    return null
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const { text, userName, userDisplayName, avatarUrl, media } = draft.retweet

  const retweetAttachmentClassName = disabled
    ? styles.disabledRetweetAttachment
    : styles.retweetAttachment
  const message = 'Reposted by you'

  const renderMedia = () => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (!media || media.length === 0) return null

    return (
      <div className={styles.retweetMedia}>
        <div className={styles.retweetMediaGrid}>
          {/* eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access */}
          {media.map((mediaItem: RetweetMedia, index: number) => {
            if (mediaItem.type === 'photo') {
              return (
                <div
                  key={mediaItem.id || index}
                  className={styles.retweetMediaItem}
                >
                  <img
                    src={mediaItem.url}
                    alt="Tweet media"
                    className={styles.retweetImage}
                  />
                </div>
              )
            } else if (
              mediaItem.type === 'video' ||
              mediaItem.type === 'animated_gif'
            ) {
              // For videos, use the thumbnail URL or the main URL as fallback
              const thumbnailUrl = mediaItem.url

              return (
                <div
                  key={mediaItem.id || index}
                  className={styles.retweetMediaItem}
                >
                  <div className={styles.retweetVideoPreview}>
                    <img
                      src={thumbnailUrl}
                      alt={
                        mediaItem.type === 'animated_gif'
                          ? 'Animated GIF'
                          : 'Video thumbnail'
                      }
                      className={styles.retweetVideoThumbnail}
                    />
                    {mediaItem.type === 'video' && (
                      <div className={styles.videoPlayIndicator} />
                    )}
                  </div>
                </div>
              )
            }
            return null
          })}
        </div>
      </div>
    )
  }

  return (
    <div className={retweetAttachmentClassName}>
      <div className={styles.retweetLabelContainer}>
        <RetweetIcon className={styles.retweetIcon} size="small" />
        <span className={styles.retweetLabel}>{message}</span>
      </div>
      <div className={styles.retweetAttachmentRow}>
        <img
          src={avatarUrl}
          alt={"Retweeted user's avatar"}
          className={styles.avatar}
        />
        <div className={styles.retweetAttachmentContent}>
          <span className={styles.userDisplayName}>{userDisplayName}</span>
          <span className={styles.userName}>@{userName}</span>
          <p className={styles.text}>{text}</p>
        </div>
      </div>

      {renderMedia()}

      {!disabled && (
        <Tooltip content="Disable Native Retweet Mode">
          <StyledCloseButton
            onClick={onCloseButtonClick}
            label="Disable Native Retweet Mode"
            light
          />
        </Tooltip>
      )}
    </div>
  )
}

export default RetweetAttachment
